import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert, Spinner, Badge, Collapse } from 'react-bootstrap';
import Autosuggest from 'react-autosuggest';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'; // Import useParams
import { FaTimes, FaSave, FaChevronDown, FaChevronUp, FaQuestionCircle, FaPlus } from 'react-icons/fa'; // Import FaSave
import './RoutingGuide.scss';
import Dropdown from 'react-bootstrap/Dropdown'; // Import Dropdown
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RouteComponent } from './RouteComponent'; // Import RouteComponent
import DaysOfWeek from "../../components/DaysOfWeek"; // Import DaysOfWeek component
import CommonErrorMessages from "../../components/CommonErrorMessages"; // Import CommonErrorMessages component

const AddEditDCCRouteForm = ({ setSuccessMessage }) => {

  const { routing_guide_id } = useParams(); // Retrieve provider_guide_id from URL param
  const provider_guide_id = routing_guide_id;
  const [searchParams] = useSearchParams();
  const providerType = searchParams.get('providerType'); // Retrieve providerType from URL query param
  const navigate = useNavigate();

  const sailingPriorityOptions = [
    { key: "Second Main", value: "SECOND_MAIN" },
    { key: "Main Backup", value: "MAIN_BACKUP" },
    { key: "Second Main Backup", value: "SECOND_MAIN_BACKUP" }
  ];

  /* Important flags to deterimine Add or Edit behavior of this form */
  const isAddForm = !provider_guide_id; // Determine if this is an add form
  const isEditForm = !isAddForm; // Determine if this is an edit form

  // Helper function to check if a date is more than 1 day in the past
  const isMoreThanOneDayInPast = (dateStr) => {
    if (!dateStr) return false;
    
    const inputDate = new Date(dateStr);
    const today = new Date();
    // Set today to midnight (start of day)
    today.setHours(0, 0, 0, 0);
    
    // Subtract 1 day (86400000 milliseconds = 1 day)
    const oneDayAgo = new Date(today.getTime() - 86400000);
    
    // Return true if inputDate is before oneDayAgo
    return inputDate < oneDayAgo;
  };

  /* If isAddForm is true, do not load the FutureRoute data or tab */
  /* If isAddForm is false, load both LiveRoute and FutureRoute data and tabs */

  /* Define days of the week as an array of weekdays (ie: Monday, Tuesday, etc.) */
  const daysOfWeek = DaysOfWeek(); // Import days of the week from DaysOfWeek component

  const errors = CommonErrorMessages(); // Import common error messages from CommonErrorMessages component

  /* Define an instance of RouteComponent for the 'Live' tab. Import all handlers and functions returned by RouteComponent but set a 'live_' prefix to each handler name to differentiation from other tabs. */
  const {
    Route: LiveRoute,
    setRoute: setLiveRoute,
    fetchRouteData: fetchLiveRouteData,
    handlePortOfLoadingSelect: live_handlePortOfLoadingSelect,
    handlePortOfDischargeSelect: live_handlePortOfDischargeSelect,
    handleCarrierServiceSection_ServiceChange: live_handleCarrierServiceSection_ServiceChange,
    handleAddCarrierService: live_handleAddCarrierService,
    handleRemoveCarrierSection: live_handleRemoveCarrierSection,
    toggleCutoffSection: live_toggleCutoffSection,
    setTimeFromString: live_setTimeFromString,
    validateForm: live_validateForm,
    handleSaveEditRoute: live_handleSaveEditRoute,
    fetchPortSuggestionsExtended: live_fetchPortSuggestionsExtended,
    fetchPortSuggestionsForCFSPOL: live_fetchPortSuggestionsForCFSPOL,
    fetchPortSuggestionsForCFSPOD: live_fetchPortSuggestionsForCFSPOD,
    handleOpenCoLoadModal: live_handleOpenCoLoadModal,
    handleCloseCoLoadModal: live_handleCloseCoLoadModal,
    getTokenizedCombinations: live_getTokenizedCombinations,
    handleConvertToCoload: live_handleConvertToCoload,
    allCarrierSectionsHaveCarriers: live_allCarrierSectionsHaveCarriers
  } = RouteComponent(provider_guide_id, 'LIVE'); // Initialize RouteComponent

  /* Define an instance of RouteComponent for the 'Future' tab. Import all handlers and functions returned by RouteComponent but set a 'future_' prefix to each handler name to differentiation from other tabs. */
  const {
    Route: FutureRoute,
    setRoute: setFutureRoute,
    fetchRouteData: fetchFutureRouteData,
    handlePortOfLoadingSelect: future_handlePortOfLoadingSelect,
    handlePortOfDischargeSelect: future_handlePortOfDischargeSelect,
    handleCarrierServiceSection_ServiceChange: future_handleCarrierServiceSection_ServiceChange,
    handleAddCarrierService: future_handleAddCarrierService,
    handleRemoveCarrierSection: future_handleRemoveCarrierSection,
    toggleCutoffSection: future_toggleCutoffSection,
    setTimeFromString: future_setTimeFromString,
    validateForm: future_validateForm,
    handleSaveEditRoute: future_handleSaveEditRoute,
    fetchPortSuggestionsExtended: future_fetchPortSuggestionsExtended,
    fetchPortSuggestionsForCFSPOL: future_fetchPortSuggestionsForCFSPOL,
    fetchPortSuggestionsForCFSPOD: future_fetchPortSuggestionsForCFSPOD,
    handleOpenCoLoadModal: future_handleOpenCoLoadModal,
    handleCloseCoLoadModal: future_handleCloseCoLoadModal,
    getTokenizedCombinations: future_getTokenizedCombinations,
    handleConvertToCoload: future_handleConvertToCoload,
    allCarrierSectionsHaveCarriers: future_allCarrierSectionsHaveCarriers
  } = RouteComponent(provider_guide_id, 'FUTURE'); // Initialize RouteComponent

  /* For tab switching */
  const [selectedTab, setSelectedTab] = useState("Live"); // Added for tab selection
  
  /* Trigger a useEffect if LiveRoute.has_future_version changes */
  useEffect(() => {
      if (LiveRoute.has_future_version) {
        fetchFutureRouteData();
      }
  }, [LiveRoute.has_future_version]);

  return (
    <div>
      <Button variant="secondary" onClick={() => navigate(-1)} className="mb-3">
        Back
      </Button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{isAddForm ? "Create New Consol Route" : "Edit Consol Route"}</h1>
        <Dropdown>
          <Dropdown.Toggle variant="secondary" id="dropdown-basic">
            Options
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {!LiveRoute.routeNotFound && (
              <Dropdown.Item onClick={live_handleOpenCoLoadModal}>
                Convert to Co-Load Route(s)
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="tab-buttons-bar" style={{ alignItems: 'baseline' }}>
        <Button
          className={selectedTab === "Live" ? "tab-button-current" : "tab-button-inactive"}
          onClick={() => setSelectedTab("Live")}
        >
          Live
        </Button>
        {/* Only show the Future tab if LiveRoute.has_future_version is true */}
        {LiveRoute.has_future_version && (
          <Button
            className={selectedTab === "Future" ? "tab-button-current" : "tab-button-inactive"}
            onClick={() => setSelectedTab("Future")}
          >
            Future
          </Button>
        )}
      </div>

      {selectedTab === "Live" && (
        <div className="form-section tab-content">
          {/* Add a spot for a success message if LiveRoute.successMessage is set */}
          {LiveRoute.successMessage && (
            <Alert variant="success" onClose={() => setLiveRoute(prev => ({ ...prev, successMessage: '' }))} dismissible>
              {LiveRoute.successMessage}
            </Alert>
          )}
          {LiveRoute.error && <Alert variant="danger" onClose={() => setLiveRoute(prev => ({ ...prev, error: '' }))} dismissible>{LiveRoute.error}</Alert>}
          {LiveRoute.existingConversionRequest && (
            <Alert variant="info">
              <p>
                {LiveRoute.existingConversionRequest.due_for_conversion ? (
                  <span>
                    This route is due for `conversion to Co-Load route(s) in {LiveRoute.existingConversionRequest.days_until_conversion} day(s). Click the <strong>Convert to Co-Load Route(s)</strong> option to edit the request.
                  </span>
                ) : LiveRoute.existingConversionRequest.converted ? (
                  <span>
                    This route has been converted to Co-Load route(s) on {LiveRoute.existingConversionRequest.converted_date}.
                  </span>
                ) : (
                  <span>This route has an existing conversion request and is either in the process of conversion or has already converted. You will not be able to make any further edits to this DCC Route. <a href={`/routing-guide/list?providerType=${providerType}`}>Back to DCC Routes</a></span>
                )}
              </p>
            </Alert>
          )}
          <div className="form-section shadow-sm row">
            <div className="col-md-6">
              <div className="form-section">
                <Form.Group controlId="portOfLoading" className="mb-3">
                  <Form.Label>Port of Loading</Form.Label>
                  {isEditForm ? (
                    <>
                      <Form.Control
                        type="text"
                        value={LiveRoute.selectedPortOfLoading}
                        disabled
                        readOnly
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Port of Loading cannot be changed in edit mode.
                      </Form.Text>
                    </>
                  ) : (
                    <Autosuggest
                      suggestions={LiveRoute.availablePortOfLoading}
                      onSuggestionsFetchRequested={({ value }) => live_fetchPortSuggestionsExtended(value, 'availablePortOfLoading')}
                      onSuggestionsClearRequested={() => setLiveRoute(prev => ({ ...prev, availablePortOfLoading: [] }))}
                      getSuggestionValue={suggestion => suggestion.port_code}
                      renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                      inputProps={{
                        placeholder: 'Enter Port of Loading',
                        value: LiveRoute.selectedPortOfLoading,
                        onChange: (e, { newValue }) => setLiveRoute(prev => ({ ...prev, selectedPortOfLoading: newValue }))
                      }}
                      onSuggestionSelected={live_handlePortOfLoadingSelect}
                      theme={{
                        container: 'react-autosuggest__container',
                        input: 'react-autosuggest__input',
                        suggestionsContainer: 'react-autosuggest__suggestions-container',
                        suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                        suggestionsList: 'react-autosuggest__suggestions-list',
                        suggestion: 'react-autosuggest__suggestion',
                        suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                      }}
                    />
                  )}
                  {LiveRoute.missingPortOfLoadingError && (
                    <Form.Text className="text-muted text-warning">
                      {LiveRoute.missingPortOfLoadingError}
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group controlId="portOfDischarge" className="mb-3">
                  <Form.Label>Port of Discharge</Form.Label>
                  {isEditForm ? (
                    <>
                      <Form.Control
                        type="text"
                        value={LiveRoute.selectedPortOfDischarge}
                        disabled
                        readOnly
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Port of Discharge cannot be changed in edit mode.
                      </Form.Text>
                    </>
                  ) : (
                    <Autosuggest
                      suggestions={LiveRoute.availablePortOfDischarge}
                      onSuggestionsFetchRequested={({ value }) => live_fetchPortSuggestionsExtended(value, 'availablePortOfDischarge')}
                      onSuggestionsClearRequested={() => setLiveRoute(prev => ({ ...prev, availablePortOfDischarge: [] }))}
                      getSuggestionValue={suggestion => suggestion.port_code}
                      renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                      inputProps={{
                        placeholder: 'Enter Port of Discharge',
                        value: LiveRoute.selectedPortOfDischarge,
                        onChange: (e, { newValue }) => setLiveRoute(prev => ({ ...prev, selectedPortOfDischarge: newValue }))
                      }}
                      onSuggestionSelected={live_handlePortOfDischargeSelect}
                      theme={{
                        container: 'react-autosuggest__container',
                        input: 'react-autosuggest__input',
                        suggestionsContainer: 'react-autosuggest__suggestions-container',
                        suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                        suggestionsList: 'react-autosuggest__suggestions-list',
                        suggestion: 'react-autosuggest__suggestion',
                        suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                      }}
                    />
                  )}
                  {LiveRoute.missingPortOfDischargeError && (
                    <Form.Text className="text-muted text-warning">
                      {LiveRoute.missingPortOfDischargeError}
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-section">
                <Form.Group controlId="cfsOrigins" className="mb-3">
                  <Form.Label className="d-flex align-items-center">
                    CFS Origin(s) To POL
                    <Button
                      variant="link"
                      className="p-0 ms-2 question-mark-btn"
                      onClick={() => setLiveRoute(prev => ({ ...prev, showCFSOriginHelp: true }))}
                      style={{ textDecoration: 'none', backgroundColor: 'transparent', border: 'none' }}
                    >
                      <FaQuestionCircle />
                    </Button>
                  </Form.Label>
                  <Autosuggest
                    suggestions={(LiveRoute.cfsOrigins && LiveRoute.cfsOrigins.length) ? LiveRoute.originSuggestions.filter(suggestion => !LiveRoute.cfsOrigins.includes(suggestion.port_code)) : LiveRoute.originSuggestions}
                    onSuggestionsFetchRequested={({ value }) => {
                      if (value.trim() === "") {
                        live_fetchPortSuggestionsForCFSPOL("");
                      } else {
                        live_fetchPortSuggestionsForCFSPOL(value);
                      }
                    }}
                    onSuggestionsClearRequested={() => setLiveRoute(prev => ({ ...prev, originSuggestions: [] }))}
                    shouldRenderSuggestions={() => true}
                    getSuggestionValue={suggestion => suggestion.port_code}
                    renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                    inputProps={{
                      placeholder: 'Enter CFS Origin(s) For Specified POL',
                      value: LiveRoute.cfsOriginInput,
                      onChange: (e, { newValue }) => setLiveRoute(prev => ({ ...prev, cfsOriginInput: newValue })),
                      onFocus: () => live_fetchPortSuggestionsForCFSPOL(""),
                      onBlur: () => setLiveRoute(prev => ({ ...prev, originSuggestions: [] })),
                      ref: LiveRoute.cfsOriginInputRef
                    }}
                    onSuggestionSelected={(e, { suggestion }) => {
                      e.preventDefault();
                      if (!LiveRoute.cfsOrigins?.includes(suggestion.port_code)) {
                        setLiveRoute(prev => ({ ...prev, cfsOrigins: [...LiveRoute.cfsOrigins || [], suggestion.port_code] }));
                      }
                      setLiveRoute(prev => ({ ...prev, cfsOriginInput: '' }));
                      setLiveRoute(prev => ({ ...prev, originSuggestions: [] }));
                      if (LiveRoute.cfsOriginInputRef.current) {
                        setTimeout(() => LiveRoute.cfsOriginInputRef.current.blur(), 0);
                      }
                    }}
                    theme={{
                      container: 'react-autosuggest__container',
                      input: 'react-autosuggest__input',
                      suggestionsContainer: 'react-autosuggest__suggestions-container',
                      suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                      suggestionsList: 'react-autosuggest__suggestions-list',
                      suggestion: 'react-autosuggest__suggestion',
                      suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                    }}
                  />
                  <Form.Text muted>
                    Leaving this blank will use all CFS Origin locations
                  </Form.Text>
                  <div className="selected-cfs-origins mt-2">
                    {Array.isArray(LiveRoute.cfsOrigins) && LiveRoute.cfsOrigins.map((origin, index) => (
                      <Badge key={index} bg="secondary" className="me-2">
                        {origin}
                        <FaTimes
                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                          onClick={() => setLiveRoute(prev => ({ ...prev, cfsOrigins: LiveRoute.cfsOrigins.filter(o => o !== origin) }))}
                        />
                      </Badge>
                    ))}
                  </div>
                </Form.Group>

                <Form.Group controlId="cfsDestinations" className="mb-3">
                  <Form.Label className="d-flex align-items-center">
                    CFS Destination(s) From POD
                    <Button
                      variant="link"
                      className="p-0 ms-2 question-mark-btn"
                      onClick={() => setLiveRoute(prev => ({ ...prev, showCFSDestinationHelp: true }))}
                      style={{ textDecoration: 'none', backgroundColor: 'transparent', border: 'none' }}
                    >
                      <FaQuestionCircle />
                    </Button>
                  </Form.Label>
                  <Autosuggest
                    suggestions={(LiveRoute.cfsDestinations && LiveRoute.cfsDestinations.length) ? LiveRoute.destinationSuggestions.filter(suggestion => !LiveRoute.cfsDestinations.includes(suggestion.port_code)) : LiveRoute.destinationSuggestions}
                    onSuggestionsFetchRequested={({ value }) => {
                      if (value.trim() === "") {
                        live_fetchPortSuggestionsForCFSPOD("");
                      } else {
                        live_fetchPortSuggestionsForCFSPOD(value);
                      }
                    }}
                    onSuggestionsClearRequested={() => setLiveRoute(prev => ({ ...prev, destinationSuggestions: [] }))}
                    shouldRenderSuggestions={() => true}
                    getSuggestionValue={suggestion => suggestion.port_code}
                    renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                    inputProps={{
                      placeholder: 'Enter CFS Destination(s) For Specified POD',
                      value: LiveRoute.cfsDestinationInput,
                      onChange: (e, { newValue }) => setLiveRoute(prev => ({ ...prev, cfsDestinationInput: newValue })),
                      onFocus: () => live_fetchPortSuggestionsForCFSPOD(""),
                      onBlur: () => setLiveRoute(prev => ({ ...prev, destinationSuggestions: [] })),
                      ref: LiveRoute.cfsDestinationInputRef
                    }}
                    onSuggestionSelected={(e, { suggestion }) => {
                      e.preventDefault();
                      if (!LiveRoute.cfsDestinations?.includes(suggestion.port_code)) {
                        setLiveRoute(prev => ({ ...prev, cfsDestinations: [...LiveRoute.cfsDestinations || [], suggestion.port_code] }));
                      }
                      setLiveRoute(prev => ({ ...prev, cfsDestinationInput: '' }));
                      setLiveRoute(prev => ({ ...prev, destinationSuggestions: [] }));
                      if (LiveRoute.cfsDestinationInputRef.current) {
                        setTimeout(() => LiveRoute.cfsDestinationInputRef.current.blur(), 0);
                      }
                    }}
                    theme={{
                      container: 'react-autosuggest__container',
                      input: 'react-autosuggest__input',
                      suggestionsContainer: 'react-autosuggest__suggestions-container',
                      suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                      suggestionsList: 'react-autosuggest__suggestions-list',
                      suggestion: 'react-autosuggest__suggestion',
                      suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                    }}
                  />
                  <Form.Text muted>
                    Leaving this blank will use all CFS Destination locations
                  </Form.Text>
                  <div className="selected-cfs-destinations mt-2">
                    {Array.isArray(LiveRoute.cfsDestinations) && LiveRoute.cfsDestinations.map((destination, index) => (
                      <Badge key={index} bg="secondary" className="me-2">
                        {destination}
                        <FaTimes
                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                          onClick={() => setLiveRoute(prev => ({ ...prev, cfsDestinations: LiveRoute.cfsDestinations.filter(d => d !== destination) }))}
                        />
                      </Badge>
                    ))}
                  </div>
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="form-section shadow-sm row">
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Group controlId="carrier">
                  <Form.Label>Carrier</Form.Label>
                  <Form.Control as="select"
                    value={LiveRoute.selectedCarrier}
                    onChange={(e) => setLiveRoute(prev => ({ ...prev, selectedCarrier: e.target.value }))}
                  >
                    <option value="">-- Select Carrier --</option>
                    {
                      (LiveRoute.availableCarriers || []).map(carrier => (
                        <option key={carrier.scac_code} value={carrier.scac_code}>
                          {carrier.carrier_name} ({carrier.scac_code})
                        </option>
                      ))
                    }
                  </Form.Control>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group controlId="service">
                  <Form.Label>Service</Form.Label>
                  <Form.Control as="select"
                    value={LiveRoute.selectedService}
                    onChange={(e) => setLiveRoute(prev => ({ ...prev, selectedService: e.target.value }))}
                  >
                    <option value="">-- Select Service --</option>
                    {(LiveRoute.availableServices || []).map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </Form.Control>
                  {LiveRoute.missingServiceError && (
                    <Form.Text className="text-muted">{LiveRoute.missingServiceError}</Form.Text>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group controlId="sailingPriority">
                  <Form.Label className="d-flex align-items-center">
                    Sailing Priority
                    <Button
                      variant="link"
                      className="p-0 ms-2 question-mark-btn"
                      onClick={() => setLiveRoute(prev => ({ ...prev, showSailPriorityHelp: true }))}
                      style={{ textDecoration: 'none', backgroundColor: 'transparent', border: 'none' }}
                    >
                      <FaQuestionCircle />
                    </Button>
                  </Form.Label>
                  <Form.Control as="select" value="Main" disabled readOnly>
                    <option>Main</option>
                  </Form.Control>
                </Form.Group>
              </div>
            </div>
            <div className="nested-cutoff-section">
              <Button
                variant="link"
                onClick={() => setLiveRoute(prev => ({ ...prev, showCutoffSection: !LiveRoute.showCutoffSection }))}
                className="w-100 text-start mb-3"
                style={{ textDecoration: 'none' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="mb-0 cutoff-header">Cutoff Dates and Times</h2>
                  {LiveRoute.showCutoffSection ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </Button>
              {LiveRoute.showCutoffSection && (
                <div>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group controlId="bookingCutoff" className="mb-3">
                        <Form.Label>Booking Cutoff Offset Days From Port ETD</Form.Label>
                        <Form.Control
                          type="number"
                          value={LiveRoute.bookingCutoff}
                          onChange={(e) => setLiveRoute(prev => ({ ...prev, bookingCutoff: parseInt(e.target.value, 10) }))}
                          placeholder="Enter Booking Cutoff"
                        />
                      </Form.Group>
                      <Form.Group controlId="bookingCutoffTime" className="mb-3">
                        <Form.Label>Booking Cutoff Time</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="HH"
                            value={LiveRoute.bookingCutoffTime.hh}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^(0?[0-9]|1[0-2])$/.test(value)) {
                                setLiveRoute(prev => ({ ...prev, bookingCutoffTime: { ...LiveRoute.bookingCutoffTime, hh: value } }));
                              }
                            }}
                            style={{ width: '60px', marginRight: '5px' }}
                          />
                          <span>:</span>
                          <Form.Control
                            type="text"
                            placeholder="MM"
                            value={LiveRoute.bookingCutoffTime.mm}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^[0-5]?[0-9]$/.test(value)) {
                                setLiveRoute(prev => ({ ...prev, bookingCutoffTime: { ...LiveRoute.bookingCutoffTime, mm: value } }));
                              }
                            }}
                            style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                          />
                          <Form.Select
                            value={LiveRoute.bookingCutoffTime.meridiem}
                            onChange={(e) => setLiveRoute(prev => ({ ...prev, bookingCutoffTime: { ...LiveRoute.bookingCutoffTime, meridiem: e.target.value } }))}
                            style={{ width: '80px' }}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Form.Select>
                        </div>
                      </Form.Group>
                      <Form.Group controlId="cargoCutoff" className="mb-3">
                        <Form.Label>Cargo Cutoff Offset Days From Port ETD</Form.Label>
                        <Form.Control
                          type="number"
                          value={LiveRoute.cargoCutoff}
                          onChange={(e) => setLiveRoute(prev => ({ ...prev, cargoCutoff: parseInt(e.target.value, 10) }))}
                          placeholder="Enter Cargo Cutoff"
                        />
                      </Form.Group>
                      <Form.Group controlId="cargoCutoffTime" className="mb-3">
                        <Form.Label>Cargo Cutoff Time</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="HH"
                            value={LiveRoute.cargoCutoffTime.hh}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^(0?[0-9]|1[0-2])$/.test(value)) {
                                setLiveRoute(prev => ({ ...prev, cargoCutoffTime: { ...LiveRoute.cargoCutoffTime, hh: value } }));
                              }
                            }}
                            style={{ width: '60px', marginRight: '5px' }}
                          />
                          <span>:</span>
                          <Form.Control
                            type="text"
                            placeholder="MM"
                            value={LiveRoute.cargoCutoffTime.mm}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^[0-5]?[0-9]$/.test(value)) {
                                setLiveRoute(prev => ({ ...prev, cargoCutoffTime: { ...LiveRoute.cargoCutoffTime, mm: value } }));
                              }
                            }}
                            style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                          />
                          <Form.Select
                            value={LiveRoute.cargoCutoffTime.meridiem}
                            onChange={(e) => setLiveRoute(prev => ({ ...prev, cargoCutoffTime: { ...LiveRoute.cargoCutoffTime, meridiem: e.target.value } }))}
                            style={{ width: '80px' }}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Form.Select>
                        </div>
                      </Form.Group>
                      <Form.Group controlId="documentationCutoff" className="mb-3">
                        <Form.Label>Documentation Cutoff Offset Days From Port ETD</Form.Label>
                        <Form.Control
                          type="number"
                          value={LiveRoute.documentationCutoff}
                          onChange={(e) => setLiveRoute(prev => ({ ...prev, documentationCutoff: parseInt(e.target.value, 10) }))}
                          placeholder="Enter Documentation Cutoff"
                        />
                      </Form.Group>
                      <Form.Group controlId="documentationCutoffTime" className="mb-3">
                        <Form.Label>Documentation Cutoff Time</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="HH"
                            value={LiveRoute.documentationCutoffTime.hh}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^(0?[0-9]|1[0-2])$/.test(value)) {
                                setLiveRoute(prev => ({ ...prev, documentationCutoffTime: { ...LiveRoute.documentationCutoffTime, hh: value } }));
                              }
                            }}
                            style={{ width: '60px', marginRight: '5px' }}
                          />
                          <span>:</span>
                          <Form.Control
                            type="text"
                            placeholder="MM"
                            value={LiveRoute.documentationCutoffTime.mm}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^[0-5]?[0-9]$/.test(value)) {
                                setLiveRoute(prev => ({ ...prev, documentationCutoffTime: { ...LiveRoute.documentationCutoffTime, mm: value } }));
                              }
                            }}
                            style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                          />
                          <Form.Select
                            value={LiveRoute.documentationCutoffTime.meridiem}
                            onChange={(e) => setLiveRoute(prev => ({ ...prev, documentationCutoffTime: { ...LiveRoute.documentationCutoffTime, meridiem: e.target.value } }))}
                            style={{ width: '80px' }}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Form.Select>
                        </div>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="etdWeekday" className="mb-3">
                        <Form.Label>POL ETD Weekday</Form.Label>
                        <Form.Control
                          as="select"
                          value={LiveRoute.etdWeekday}
                          onChange={(e) => setLiveRoute(prev => ({ ...prev, etdWeekday: e.target.value }))}
                        >
                          <option value="">Select ETD Weekday</option>
                          {LiveRoute.poletdweekdayoptions?.map((weekday, index) => (
                            <option value={weekday}>{weekday}</option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="etaWeekday" className="mb-3">
                        <Form.Label>POD ETA Weekday</Form.Label>
                        <Form.Control
                          as="select"
                          value={LiveRoute.etaWeekday}
                          onChange={(e) => setLiveRoute(prev => ({ ...prev, etaWeekday: e.target.value }))}
                        >
                          <option value="">Select ETA Weekday</option>
                          {LiveRoute.podetaweekdayoptions?.map((weekday, index) => (
                            <option value={weekday}>{weekday}</option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="cargoAvailabilityWeekday" className="mb-3">
                        <Form.Label>Cargo Availability at Destination CFS Weekday</Form.Label>
                        <Form.Control
                          as="select"
                          value={LiveRoute.cargoAvailabilityWeekday}
                          onChange={(e) => setLiveRoute(prev => ({ ...prev, cargoAvailabilityWeekday: e.target.value }))}
                        >
                          <option value="">Select Cargo Availability Weekday</option>
                          <option value="Monday">Monday</option>
                          <option value="Tuesday">Tuesday</option>
                          <option value="Wednesday">Wednesday</option>
                          <option value="Thursday">Thursday</option>
                          <option value="Friday">Friday</option>
                          <option value="Saturday">Saturday</option>
                          <option value="Sunday">Sunday</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="portDwellTime" className="mb-3">
                        <Form.Label>Port Dwell Time</Form.Label>
                        <Form.Control
                          type="number"
                          value={LiveRoute.portDwellTime}
                          onChange={(e) => setLiveRoute(prev => ({ ...prev, portDwellTime: e.target.value }))}
                          placeholder="Enter Port Dwell Time"
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {LiveRoute.carrierSections.map((section, index) => (
            <div
              key={section.id || index}
              className="form-section shadow-sm row"
              style={{
                position: 'relative',
                marginBottom: '15px',
                paddingTop: '30px'
              }}
            >
              {section.validationError && (
                <div style={{ textAlign: 'center', color: 'red' }}>
                  {section.validationError}
                </div>
              )}
              <Button
                variant="danger"
                size="sm"
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  zIndex: 1,
                  margin: '5px',
                  width: 'auto'
                }}
                onClick={() => live_handleRemoveCarrierSection(section.id)}
              >
                X
              </Button>
              <div className="row">
                <div className="col-md-4">
                  <Form.Group controlId={`carrier-${section.id}`}>
                    <Form.Label>Carrier</Form.Label>
                    <Form.Control as="select"
                      value={section.selectedCarrier || ''}
                      onChange={async (e) => {
                        const value = e.target.value;
                        const updatedSections = [...LiveRoute.carrierSections];
                        updatedSections[index] = { ...updatedSections[index], selectedCarrier: value };

                        if (!value) {
                          updatedSections[index].availableServices = [];
                          updatedSections[index].selectedService = '';
                        } else {
                          try {
                            const response = await axios.get('/api/services-by-scac-and-pol-pod', {
                              params: { scac_code: value, pol: LiveRoute.selectedPortOfLoading, pod: LiveRoute.selectedPortOfDischarge },
                            });
                            const services = Array.isArray(response.data)
                              ? response.data.map(service => service.service)
                              : [];
                            updatedSections[index].availableServices = services;
                            updatedSections[index].selectedService = '';
                          } catch (error) {
                            console.error('Error fetching services for carrier:', error);
                            updatedSections[index].availableServices = [];
                            updatedSections[index].selectedService = '';
                          }
                        }
                        setLiveRoute(prev => ({ ...prev, carrierSections: updatedSections }));
                      }}
                    >
                      <option value="">-- Select Carrier --</option>
                      {(section.availableCarriers || []).map(carrier => (
                        <option key={carrier.scac_code} value={carrier.scac_code}>
                          {carrier.carrier_name} ({carrier.scac_code})
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group controlId={`service-${section.id}`}>
                    <Form.Label>Service</Form.Label>
                    <Form.Control as="select"
                      value={section.selectedService || ''}
                      onChange={(e) => live_handleCarrierServiceSection_ServiceChange(e, index)}
                    >
                      <option value="">-- Select Service --</option>
                      {(section.availableServices || []).map(service => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </Form.Control>
                    {section.missingServiceError && (
                      <Form.Text className="text-muted">{section.missingServiceError}</Form.Text>
                    )}
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group controlId={`sailingPriority-${section.id}`}>
                    <Form.Label>Sailing Priority</Form.Label>
                    <Form.Control
                      as="select"
                      value={section.sailingPriority || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const updatedSections = [...LiveRoute.carrierSections];
                        if (updatedSections.some((s, idx) => idx !== index && s.sailingPriority === newValue)) {
                          updatedSections[index] = {
                            ...section,
                            sailingPriority: newValue,
                            sailingPriorityError: 'Duplicate priority selected.'
                          };
                        } else {
                          updatedSections[index] = {
                            ...section,
                            sailingPriority: newValue,
                            sailingPriorityError: ''
                          };
                        }
                        setLiveRoute(prev => ({ ...prev, carrierSections: updatedSections }));
                      }}
                    >
                      <option value="">Select Sailing Priority</option>
                      {sailingPriorityOptions.map((option) => (
                        <option key={option.key} value={option.value}>{option.key}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>
              <div className="nested-cutoff-section">
                <Button
                  variant="link"
                  onClick={() => live_toggleCutoffSection(index)}
                  className="w-100 text-start mb-3"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h2 className="mb-0 cutoff-header">Cutoff Dates and Times</h2>
                    {section.showCutoffSection ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </Button>
                {section.showCutoffSection && (
                  <div>
                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group controlId="bookingCutoff" className="mb-3">
                          <Form.Label>Booking Cutoff Offset Days From Port ETD</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.bookingCutoff || ''}
                            onChange={e => {
                              const updatedSection = { ...section, bookingCutoff: e.target.value };
                              const newSections = [...LiveRoute.carrierSections];
                              newSections[index] = updatedSection;
                              setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                            }}
                            placeholder="Enter Booking Cutoff"
                          />
                        </Form.Group>
                        <Form.Group controlId="bookingCutoffTime" className="mb-3">
                          <Form.Label>Booking Cutoff Time</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="text"
                              placeholder="HH"
                              value={section.bookingCutoffTime?.hh || '12'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  bookingCutoffTime: { ...section.bookingCutoffTime, hh: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginRight: '5px' }}
                            />
                            <span>:</span>
                            <Form.Control
                              type="text"
                              placeholder="MM"
                              value={section.bookingCutoffTime?.mm || '00'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  bookingCutoffTime: { ...section.bookingCutoffTime, mm: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                            />
                            <Form.Select
                              value={section.bookingCutoffTime?.meridiem || 'AM'}
                              onChange={(e) => {
                                const updatedSection = {
                                  ...section,
                                  bookingCutoffTime: { ...section.bookingCutoffTime, meridiem: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '80px' }}
                            >
                              <option>AM</option>
                              <option>PM</option>
                            </Form.Select>
                          </div>
                        </Form.Group>
                        <Form.Group controlId="cargoCutoff" className="mb-3">
                          <Form.Label>Cargo Cutoff Offset Days From Port ETD</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.cargoCutoff || ''}
                            onChange={e => {
                              const updatedSection = { ...section, cargoCutoff: e.target.value };
                              const newSections = [...LiveRoute.carrierSections];
                              newSections[index] = updatedSection;
                              setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                            }}
                            placeholder="Enter Cargo Cutoff"
                          />
                        </Form.Group>
                        <Form.Group controlId="cargoCutoffTime" className="mb-3">
                          <Form.Label>Cargo Cutoff Time</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="text"
                              placeholder="HH"
                              value={section.cargoCutoffTime?.hh || '12'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  cargoCutoffTime: { ...section.cargoCutoffTime, hh: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginRight: '5px' }}
                            />
                            <span>:</span>
                            <Form.Control
                              type="text"
                              placeholder="MM"
                              value={section.cargoCutoffTime?.mm || '00'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  cargoCutoffTime: { ...section.cargoCutoffTime, mm: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                            />
                            <Form.Select
                              value={section.cargoCutoffTime?.meridiem || 'AM'}
                              onChange={(e) => {
                                const updatedSection = {
                                  ...section,
                                  cargoCutoffTime: { ...section.cargoCutoffTime, meridiem: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '80px' }}
                            >
                              <option>AM</option>
                              <option>PM</option>
                            </Form.Select>
                          </div>
                        </Form.Group>
                        <Form.Group controlId="documentationCutoff" className="mb-3">
                          <Form.Label>Documentation Cutoff Offset Days From Port ETD</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.documentationCutoff || ''}
                            onChange={e => {
                              const updatedSection = { ...section, documentationCutoff: e.target.value };
                              const newSections = [...LiveRoute.carrierSections];
                              newSections[index] = updatedSection;
                              setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                            }}
                            placeholder="Enter Documentation Cutoff"
                          />
                        </Form.Group>
                        <Form.Group controlId="documentationCutoffTime" className="mb-3">
                          <Form.Label>Documentation Cutoff Time</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="text"
                              placeholder="HH"
                              value={section.documentationCutoffTime?.hh || '12'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  documentationCutoffTime: { ...section.documentationCutoffTime, hh: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginRight: '5px' }}
                            />
                            <span>:</span>
                            <Form.Control
                              type="text"
                              placeholder="MM"
                              value={section.documentationCutoffTime?.mm || '00'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  documentationCutoffTime: { ...section.documentationCutoffTime, mm: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                            />
                            <Form.Select
                              value={section.documentationCutoffTime?.meridiem || 'AM'}
                              onChange={(e) => {
                                const updatedSection = {
                                  ...section,
                                  documentationCutoffTime: { ...section.documentationCutoffTime, meridiem: e.target.value }
                                };
                                const newSections = [...LiveRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setLiveRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '80px' }}
                            >
                              <option>AM</option>
                              <option>PM</option>
                            </Form.Select>
                          </div>
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group controlId="etdWeekday">
                          <Form.Label>ETD Weekday</Form.Label>
                          <Form.Control
                            as="select"
                            value={section.etdWeekday || ''}
                            onChange={(e) => {
                              const updated = [...LiveRoute.carrierSections];
                              updated[index] = { ...updated[index], etdWeekday: e.target.value };
                              setLiveRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          >
                            <option value="">Select ETD Weekday</option>
                            {section.poletdweekdayoptions?.map((weekday) => (
                              <option key={weekday} value={weekday}>{weekday}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="etaWeekday">
                          <Form.Label>ETA Weekday</Form.Label>
                          <Form.Control
                            as="select"
                            value={section.etaWeekday || ''}
                            onChange={(e) => {
                              const updated = [...LiveRoute.carrierSections];
                              updated[index] = { ...updated[index], etaWeekday: e.target.value };
                              setLiveRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          >
                            <option value="">Select ETA Weekday</option>
                            {section.podetaweekdayoptions?.map((weekday) => (
                              <option key={weekday} value={weekday}>{weekday}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="cargoAvailabilityWeekday">
                          <Form.Label>Cargo Availability Weekday</Form.Label>
                          <Form.Control
                            as="select"
                            value={section.cargoAvailabilityWeekday || ''}
                            onChange={(e) => {
                              const updated = [...LiveRoute.carrierSections];
                              updated[index] = { ...updated[index], cargoAvailabilityWeekday: e.target.value };
                              setLiveRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          >
                            <option value="">Select Cargo Availability</option>
                            {daysOfWeek.map((day) => (
                              <option key={day} value={day}>{day}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="portDwellTime">
                          <Form.Label>Port Dwell Time</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.portDwellTime || 0}
                            onChange={(e) => {
                              const updated = [...LiveRoute.carrierSections];
                              updated[index] = { ...updated[index], portDwellTime: Number(e.target.value) };
                              setLiveRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="text-center mb-3">
            <Button
              variant="primary"
              onClick={live_handleAddCarrierService}
              disabled={LiveRoute.carrierSections.length >= 3}
              style={LiveRoute.carrierSections.length >= 3 ? { backgroundColor: 'grey', borderColor: 'grey', cursor: 'not-allowed' } : {}}
            >
              <FaPlus className="me-1" /> Add Carrier Service
            </Button>
          </div>


          {/* Form section for selecting Effective Date and Expiration Date */}
          <div className="form-section shadow-sm row mb-3">
            <div className="col-md-6">
              <Form.Group controlId="effectiveDate" className="mb-3">
                <Form.Label>Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={LiveRoute.effectiveDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const dateError = isMoreThanOneDayInPast(newDate) ? 
                      "Effective date cannot be more than 1 day in the past." : null;
                    setLiveRoute(prev => ({ 
                      ...prev, 
                      effectiveDate: newDate,
                      effectiveDateError: dateError
                    }));
                  }}
                />
                {/* Add contextual hint text based on form mode */}
                <Form.Text className="text-muted">
                  {isAddForm
                    ? "A future effective date will keep this routing guide inactive until that date."
                    : "A future effective date will schedule this routing guide to start on that date. The changes will be saved under the Future tab."
                  }
                </Form.Text>
                {LiveRoute.effectiveDateError && (
                  <Form.Text className="text-muted text-warning">
                    {LiveRoute.effectiveDateError}
                  </Form.Text>
                )}
              </Form.Group>
            </div>
          </div>


          {/* Save and Cancel buttons */}

          <Button
            variant="primary"
            onClick={() => live_handleSaveEditRoute(isAddForm ? 'add' : 'edit')}
            disabled={LiveRoute.isSaving || (LiveRoute.existingConversionRequest && (!LiveRoute.existingConversionRequest.due_for_conversion || LiveRoute.existingConversionRequest.converted)) || LiveRoute.routeNotFound}
          >
            {LiveRoute.isSaving ? <Spinner as="span" animation="border" size="sm" /> : <FaSave className="me-2" />}
            Save
          </Button>
          <Button variant="secondary" onClick={() => navigate(-1)} className="ms-2">
            <FaTimes className="me-2" />Cancel
          </Button>

        </div>
      )}
      {selectedTab === "Future" && (
        <div className="form-section tab-content">
          {FutureRoute.error && <Alert variant="danger" onClose={() => setFutureRoute(prev => ({ ...prev, error: '' }))} dismissible>{FutureRoute.error}</Alert>}
          {FutureRoute.existingConversionRequest && (
            <Alert variant="info">
              <p>
                {FutureRoute.existingConversionRequest.due_for_conversion ? (
                  <span>
                    This route is due for `conversion to Co-Load route(s) in {FutureRoute.existingConversionRequest.days_until_conversion} day(s). Click the <strong>Convert to Co-Load Route(s)</strong> option to edit the request.
                  </span>
                ) : FutureRoute.existingConversionRequest.converted ? (
                  <span>
                    This route has been converted to Co-Load route(s) on {FutureRoute.existingConversionRequest.converted_date}.
                  </span>
                ) : (
                  <span>This route has an existing conversion request and is either in the process of conversion or has already converted. You will not be able to make any further edits to this DCC Route. <a href={`/routing-guide/list?providerType=${providerType}`}>Back to DCC Routes</a></span>
                )}
              </p>
            </Alert>
          )}
          <div className="form-section shadow-sm row">
            <div className="col-md-6">
              <div className="form-section">
                <Form.Group controlId="portOfLoading" className="mb-3">
                  <Form.Label>Port of Loading</Form.Label>
                  {isEditForm ? (
                    <>
                      <Form.Control
                        type="text"
                        value={FutureRoute.selectedPortOfLoading}
                        disabled
                        readOnly
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Port of Loading cannot be changed in edit mode.
                      </Form.Text>
                    </>
                  ) : (
                    <Autosuggest
                      suggestions={FutureRoute.availablePortOfLoading}
                      onSuggestionsFetchRequested={({ value }) => future_fetchPortSuggestionsExtended(value, 'availablePortOfLoading')}
                      onSuggestionsClearRequested={() => setFutureRoute(prev => ({ ...prev, availablePortOfLoading: [] }))}
                      getSuggestionValue={suggestion => suggestion.port_code}
                      renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                      inputProps={{
                        placeholder: 'Enter Port of Loading',
                        value: FutureRoute.selectedPortOfLoading,
                        onChange: (e, { newValue }) => setFutureRoute(prev => ({ ...prev, selectedPortOfLoading: newValue }))
                      }}
                      onSuggestionSelected={future_handlePortOfLoadingSelect}
                      theme={{
                        container: 'react-autosuggest__container',
                        input: 'react-autosuggest__input',
                        suggestionsContainer: 'react-autosuggest__suggestions-container',
                        suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                        suggestionsList: 'react-autosuggest__suggestions-list',
                        suggestion: 'react-autosuggest__suggestion',
                        suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                      }}
                    />
                  )}
                  {FutureRoute.missingPortOfLoadingError && (
                    <Form.Text className="text-muted text-warning">
                      {FutureRoute.missingPortOfLoadingError}
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group controlId="portOfDischarge" className="mb-3">
                  <Form.Label>Port of Discharge</Form.Label>
                  {isEditForm ? (
                    <>
                      <Form.Control
                        type="text"
                        value={FutureRoute.selectedPortOfDischarge}
                        disabled
                        readOnly
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Port of Discharge cannot be changed in edit mode.
                      </Form.Text>
                    </>
                  ) : (
                    <Autosuggest
                      suggestions={FutureRoute.availablePortOfDischarge}
                      onSuggestionsFetchRequested={({ value }) => future_fetchPortSuggestionsExtended(value, 'availablePortOfDischarge')}
                      onSuggestionsClearRequested={() => setFutureRoute(prev => ({ ...prev, availablePortOfDischarge: [] }))}
                      getSuggestionValue={suggestion => suggestion.port_code}
                      renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                      inputProps={{
                        placeholder: 'Enter Port of Discharge',
                        value: FutureRoute.selectedPortOfDischarge,
                        onChange: (e, { newValue }) => setFutureRoute(prev => ({ ...prev, selectedPortOfDischarge: newValue }))
                      }}
                      onSuggestionSelected={future_handlePortOfDischargeSelect}
                      theme={{
                        container: 'react-autosuggest__container',
                        input: 'react-autosuggest__input',
                        suggestionsContainer: 'react-autosuggest__suggestions-container',
                        suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                        suggestionsList: 'react-autosuggest__suggestions-list',
                        suggestion: 'react-autosuggest__suggestion',
                        suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                      }}
                    />
                  )}
                  {FutureRoute.missingPortOfDischargeError && (
                    <Form.Text className="text-muted text-warning">
                      {FutureRoute.missingPortOfDischargeError}
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-section">
                <Form.Group controlId="cfsOrigins" className="mb-3">
                  <Form.Label className="d-flex align-items-center">
                    CFS Origin(s) To POL
                    <Button
                      variant="link"
                      className="p-0 ms-2 question-mark-btn"
                      onClick={() => setFutureRoute(prev => ({ ...prev, showCFSOriginHelp: true }))}
                      style={{ textDecoration: 'none', backgroundColor: 'transparent', border: 'none' }}
                    >
                      <FaQuestionCircle />
                    </Button>
                  </Form.Label>
                  <Autosuggest
                    suggestions={(FutureRoute.cfsOrigins && FutureRoute.cfsOrigins.length) ? FutureRoute.originSuggestions.filter(suggestion => !FutureRoute.cfsOrigins.includes(suggestion.port_code)) : FutureRoute.originSuggestions}
                    onSuggestionsFetchRequested={({ value }) => {
                      if (value.trim() === "") {
                        future_fetchPortSuggestionsForCFSPOL("");
                      } else {
                        future_fetchPortSuggestionsForCFSPOL(value);
                      }
                    }}
                    onSuggestionsClearRequested={() => setFutureRoute(prev => ({ ...prev, originSuggestions: [] }))}
                    shouldRenderSuggestions={() => true}
                    getSuggestionValue={suggestion => suggestion.port_code}
                    renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                    inputProps={{
                      placeholder: 'Enter CFS Origin(s) For Specified POL',
                      value: FutureRoute.cfsOriginInput,
                      onChange: (e, { newValue }) => setFutureRoute(prev => ({ ...prev, cfsOriginInput: newValue })),
                      onFocus: () => future_fetchPortSuggestionsForCFSPOL(""),
                      onBlur: () => setFutureRoute(prev => ({ ...prev, originSuggestions: [] })),
                      ref: FutureRoute.cfsOriginInputRef
                    }}
                    onSuggestionSelected={(e, { suggestion }) => {
                      e.preventDefault();
                      if (!FutureRoute.cfsOrigins?.includes(suggestion.port_code)) {
                        setFutureRoute(prev => ({ ...prev, cfsOrigins: [...FutureRoute.cfsOrigins || [], suggestion.port_code] }));
                      }
                      setFutureRoute(prev => ({ ...prev, cfsOriginInput: '' }));
                      setFutureRoute(prev => ({ ...prev, originSuggestions: [] }));
                      if (FutureRoute.cfsOriginInputRef.current) {
                        setTimeout(() => FutureRoute.cfsOriginInputRef.current.blur(), 0);
                      }
                    }}
                    theme={{
                      container: 'react-autosuggest__container',
                      input: 'react-autosuggest__input',
                      suggestionsContainer: 'react-autosuggest__suggestions-container',
                      suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                      suggestionsList: 'react-autosuggest__suggestions-list',
                      suggestion: 'react-autosuggest__suggestion',
                      suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                    }}
                  />
                  <Form.Text muted>
                    Leaving this blank will use all CFS Origin locations
                  </Form.Text>
                  <div className="selected-cfs-origins mt-2">
                    {Array.isArray(FutureRoute.cfsOrigins) && FutureRoute.cfsOrigins.map((origin, index) => (
                      <Badge key={index} bg="secondary" className="me-2">
                        {origin}
                        <FaTimes
                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                          onClick={() => setFutureRoute(prev => ({ ...prev, cfsOrigins: FutureRoute.cfsOrigins.filter(o => o !== origin) }))}
                        />
                      </Badge>
                    ))}
                  </div>
                </Form.Group>

                <Form.Group controlId="cfsDestinations" className="mb-3">
                  <Form.Label className="d-flex align-items-center">
                    CFS Destination(s) From POD
                    <Button
                      variant="link"
                      className="p-0 ms-2 question-mark-btn"
                      onClick={() => setFutureRoute(prev => ({ ...prev, showCFSDestinationHelp: true }))}
                      style={{ textDecoration: 'none', backgroundColor: 'transparent', border: 'none' }}
                    >
                      <FaQuestionCircle />
                    </Button>
                  </Form.Label>
                  <Autosuggest
                    suggestions={(FutureRoute.cfsDestinations && FutureRoute.cfsDestinations.length) ? FutureRoute.destinationSuggestions.filter(suggestion => !FutureRoute.cfsDestinations.includes(suggestion.port_code)) : FutureRoute.destinationSuggestions}
                    onSuggestionsFetchRequested={({ value }) => {
                      if (value.trim() === "") {
                        future_fetchPortSuggestionsForCFSPOD("");
                      } else {
                        future_fetchPortSuggestionsForCFSPOD(value);
                      }
                    }}
                    onSuggestionsClearRequested={() => setFutureRoute(prev => ({ ...prev, destinationSuggestions: [] }))}
                    shouldRenderSuggestions={() => true}
                    getSuggestionValue={suggestion => suggestion.port_code}
                    renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
                    inputProps={{
                      placeholder: 'Enter CFS Destination(s) For Specified POD',
                      value: FutureRoute.cfsDestinationInput,
                      onChange: (e, { newValue }) => setFutureRoute(prev => ({ ...prev, cfsDestinationInput: newValue })),
                      onFocus: () => future_fetchPortSuggestionsForCFSPOD(""),
                      onBlur: () => setFutureRoute(prev => ({ ...prev, destinationSuggestions: [] })),
                      ref: FutureRoute.cfsDestinationInputRef
                    }}
                    onSuggestionSelected={(e, { suggestion }) => {
                      e.preventDefault();
                      if (!FutureRoute.cfsDestinations?.includes(suggestion.port_code)) {
                        setFutureRoute(prev => ({ ...prev, cfsDestinations: [...FutureRoute.cfsDestinations || [], suggestion.port_code] }));
                      }
                      setFutureRoute(prev => ({ ...prev, cfsDestinationInput: '' }));
                      setFutureRoute(prev => ({ ...prev, destinationSuggestions: [] }));
                      if (FutureRoute.cfsDestinationInputRef.current) {
                        setTimeout(() => FutureRoute.cfsDestinationInputRef.current.blur(), 0);
                      }
                    }}
                    theme={{
                      container: 'react-autosuggest__container',
                      input: 'react-autosuggest__input',
                      suggestionsContainer: 'react-autosuggest__suggestions-container',
                      suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                      suggestionsList: 'react-autosuggest__suggestions-list',
                      suggestion: 'react-autosuggest__suggestion',
                      suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                    }}
                  />
                  <Form.Text muted>
                    Leaving this blank will use all CFS Destination locations
                  </Form.Text>
                  <div className="selected-cfs-destinations mt-2">
                    {Array.isArray(FutureRoute.cfsDestinations) && FutureRoute.cfsDestinations.map((destination, index) => (
                      <Badge key={index} bg="secondary" className="me-2">
                        {destination}
                        <FaTimes
                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                          onClick={() => setFutureRoute(prev => ({ ...prev, cfsDestinations: FutureRoute.cfsDestinations.filter(d => d !== destination) }))}
                        />
                      </Badge>
                    ))}
                  </div>
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="form-section shadow-sm row">
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Group controlId="carrier">
                  <Form.Label>Carrier</Form.Label>
                  <Form.Control as="select"
                    value={FutureRoute.selectedCarrier}
                    onChange={(e) => setFutureRoute(prev => ({ ...prev, selectedCarrier: e.target.value }))}
                  >
                    <option value="">-- Select Carrier --</option>
                    {
                      (FutureRoute.availableCarriers || []).map(carrier => (
                        <option key={carrier.scac_code} value={carrier.scac_code}>
                          {carrier.carrier_name} ({carrier.scac_code})
                        </option>
                      ))
                    }
                  </Form.Control>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group controlId="service">
                  <Form.Label>Service</Form.Label>
                  <Form.Control as="select"
                    value={FutureRoute.selectedService}
                    onChange={(e) => setFutureRoute(prev => ({ ...prev, selectedService: e.target.value }))}
                  >
                    <option value="">-- Select Service --</option>
                    {(FutureRoute.availableServices || []).map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </Form.Control>
                  {FutureRoute.missingServiceError && (
                    <Form.Text className="text-muted">{FutureRoute.missingServiceError}</Form.Text>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group controlId="sailingPriority">
                  <Form.Label className="d-flex align-items-center">
                    Sailing Priority
                    <Button
                      variant="link"
                      className="p-0 ms-2 question-mark-btn"
                      onClick={() => setFutureRoute(prev => ({ ...prev, showSailPriorityHelp: true }))}
                      style={{ textDecoration: 'none', backgroundColor: 'transparent', border: 'none' }}
                    >
                      <FaQuestionCircle />
                    </Button>
                  </Form.Label>
                  <Form.Control as="select" value="Main" disabled readOnly>
                    <option>Main</option>
                  </Form.Control>
                </Form.Group>
              </div>
            </div>
            <div className="nested-cutoff-section">
              <Button
                variant="link"
                onClick={() => setFutureRoute(prev => ({ ...prev, showCutoffSection: !FutureRoute.showCutoffSection }))}
                className="w-100 text-start mb-3"
                style={{ textDecoration: 'none' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="mb-0 cutoff-header">Cutoff Dates and Times</h2>
                  {FutureRoute.showCutoffSection ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </Button>
              {FutureRoute.showCutoffSection && (
                <div>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group controlId="bookingCutoff" className="mb-3">
                        <Form.Label>Booking Cutoff Offset Days From Port ETD</Form.Label>
                        <Form.Control
                          type="number"
                          value={FutureRoute.bookingCutoff}
                          onChange={(e) => setFutureRoute(prev => ({ ...prev, bookingCutoff: parseInt(e.target.value, 10) }))}
                          placeholder="Enter Booking Cutoff"
                        />
                      </Form.Group>
                      <Form.Group controlId="bookingCutoffTime" className="mb-3">
                        <Form.Label>Booking Cutoff Time</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="HH"
                            value={FutureRoute.bookingCutoffTime.hh}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^(0?[0-9]|1[0-2])$/.test(value)) {
                                setFutureRoute(prev => ({ ...prev, bookingCutoffTime: { ...FutureRoute.bookingCutoffTime, hh: value } }));
                              }
                            }}
                            style={{ width: '60px', marginRight: '5px' }}
                          />
                          <span>:</span>
                          <Form.Control
                            type="text"
                            placeholder="MM"
                            value={FutureRoute.bookingCutoffTime.mm}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^[0-5]?[0-9]$/.test(value)) {
                                setFutureRoute(prev => ({ ...prev, bookingCutoffTime: { ...FutureRoute.bookingCutoffTime, mm: value } }));
                              }
                            }}
                            style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                          />
                          <Form.Select
                            value={FutureRoute.bookingCutoffTime.meridiem}
                            onChange={(e) => setFutureRoute(prev => ({ ...prev, bookingCutoffTime: { ...FutureRoute.bookingCutoffTime, meridiem: e.target.value } }))}
                            style={{ width: '80px' }}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Form.Select>
                        </div>
                      </Form.Group>
                      <Form.Group controlId="cargoCutoff" className="mb-3">
                        <Form.Label>Cargo Cutoff Offset Days From Port ETD</Form.Label>
                        <Form.Control
                          type="number"
                          value={FutureRoute.cargoCutoff}
                          onChange={(e) => setFutureRoute(prev => ({ ...prev, cargoCutoff: parseInt(e.target.value, 10) }))}
                          placeholder="Enter Cargo Cutoff"
                        />
                      </Form.Group>
                      <Form.Group controlId="cargoCutoffTime" className="mb-3">
                        <Form.Label>Cargo Cutoff Time</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="HH"
                            value={FutureRoute.cargoCutoffTime.hh}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^(0?[0-9]|1[0-2])$/.test(value)) {
                                setFutureRoute(prev => ({ ...prev, cargoCutoffTime: { ...FutureRoute.cargoCutoffTime, hh: value } }));
                              }
                            }}
                            style={{ width: '60px', marginRight: '5px' }}
                          />
                          <span>:</span>
                          <Form.Control
                            type="text"
                            placeholder="MM"
                            value={FutureRoute.cargoCutoffTime.mm}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^[0-5]?[0-9]$/.test(value)) {
                                setFutureRoute(prev => ({ ...prev, cargoCutoffTime: { ...FutureRoute.cargoCutoffTime, mm: value } }));
                              }
                            }}
                            style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                          />
                          <Form.Select
                            value={FutureRoute.cargoCutoffTime.meridiem}
                            onChange={(e) => setFutureRoute(prev => ({ ...prev, cargoCutoffTime: { ...FutureRoute.cargoCutoffTime, meridiem: e.target.value } }))}
                            style={{ width: '80px' }}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Form.Select>
                        </div>
                      </Form.Group>
                      <Form.Group controlId="documentationCutoff" className="mb-3">
                        <Form.Label>Documentation Cutoff Offset Days From Port ETD</Form.Label>
                        <Form.Control
                          type="number"
                          value={FutureRoute.documentationCutoff}
                          onChange={(e) => setFutureRoute(prev => ({ ...prev, documentationCutoff: parseInt(e.target.value, 10) }))}
                          placeholder="Enter Documentation Cutoff"
                        />
                      </Form.Group>
                      <Form.Group controlId="documentationCutoffTime" className="mb-3">
                        <Form.Label>Documentation Cutoff Time</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="HH"
                            value={FutureRoute.documentationCutoffTime.hh}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^(0?[0-9]|1[0-2])$/.test(value)) {
                                setFutureRoute(prev => ({ ...prev, documentationCutoffTime: { ...FutureRoute.documentationCutoffTime, hh: value } }));
                              }
                            }}
                            style={{ width: '60px', marginRight: '5px' }}
                          />
                          <span>:</span>
                          <Form.Control
                            type="text"
                            placeholder="MM"
                            value={FutureRoute.documentationCutoffTime.mm}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^[0-5]?[0-9]$/.test(value)) {
                                setFutureRoute(prev => ({ ...prev, documentationCutoffTime: { ...FutureRoute.documentationCutoffTime, mm: value } }));
                              }
                            }}
                            style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                          />
                          <Form.Select
                            value={FutureRoute.documentationCutoffTime.meridiem}
                            onChange={(e) => setFutureRoute(prev => ({ ...prev, documentationCutoffTime: { ...FutureRoute.documentationCutoffTime, meridiem: e.target.value } }))}
                            style={{ width: '80px' }}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Form.Select>
                        </div>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="etdWeekday" className="mb-3">
                        <Form.Label>POL ETD Weekday</Form.Label>
                        <Form.Control
                          as="select"
                          value={FutureRoute.etdWeekday}
                          onChange={(e) => setFutureRoute(prev => ({ ...prev, etdWeekday: e.target.value }))}
                        >
                          <option value="">Select ETD Weekday</option>
                          {FutureRoute.poletdweekdayoptions?.map((weekday, index) => (
                            <option value={weekday}>{weekday}</option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="etaWeekday" className="mb-3">
                        <Form.Label>POD ETA Weekday</Form.Label>
                        <Form.Control
                          as="select"
                          value={FutureRoute.etaWeekday}
                          onChange={(e) => setFutureRoute(prev => ({ ...prev, etaWeekday: e.target.value }))}
                        >
                          <option value="">Select ETA Weekday</option>
                          {FutureRoute.podetaweekdayoptions?.map((weekday, index) => (
                            <option value={weekday}>{weekday}</option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="cargoAvailabilityWeekday" className="mb-3">
                        <Form.Label>Cargo Availability at Destination CFS Weekday</Form.Label>
                        <Form.Control
                          as="select"
                          value={FutureRoute.cargoAvailabilityWeekday}
                          onChange={(e) => setFutureRoute(prev => ({ ...prev, cargoAvailabilityWeekday: e.target.value }))}
                        >
                          <option value="">Select Cargo Availability Weekday</option>
                          <option value="Monday">Monday</option>
                          <option value="Tuesday">Tuesday</option>
                          <option value="Wednesday">Wednesday</option>
                          <option value="Thursday">Thursday</option>
                          <option value="Friday">Friday</option>
                          <option value="Saturday">Saturday</option>
                          <option value="Sunday">Sunday</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="portDwellTime" className="mb-3">
                        <Form.Label>Port Dwell Time</Form.Label>
                        <Form.Control
                          type="number"
                          value={FutureRoute.portDwellTime}
                          onChange={(e) => setFutureRoute(prev => ({ ...prev, portDwellTime: e.target.value }))}
                          placeholder="Enter Port Dwell Time"
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {FutureRoute.carrierSections.map((section, index) => (
            <div
              key={section.id || index}
              className="form-section shadow-sm row"
              style={{
                position: 'relative',
                marginBottom: '15px',
                paddingTop: '30px'
              }}
            >
              {section.validationError && (
                <div style={{ textAlign: 'center', color: 'red' }}>
                  {section.validationError}
                </div>
              )}
              <Button
                variant="danger"
                size="sm"
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  zIndex: 1,
                  margin: '5px',
                  width: 'auto'
                }}
                onClick={() => future_handleRemoveCarrierSection(section.id)}
              >
                X
              </Button>
              <div className="row">
                <div className="col-md-4">
                  <Form.Group controlId={`carrier-${section.id}`}>
                    <Form.Label>Carrier</Form.Label>
                    <Form.Control as="select"
                      value={section.selectedCarrier || ''}
                      onChange={async (e) => {
                        const value = e.target.value;
                        const updatedSections = [...FutureRoute.carrierSections];
                        updatedSections[index] = { ...updatedSections[index], selectedCarrier: value };

                        if (!value) {
                          updatedSections[index].availableServices = [];
                          updatedSections[index].selectedService = '';
                        } else {
                          try {
                            const response = await axios.get('/api/services-by-scac-and-pol-pod', {
                              params: { scac_code: value, pol: FutureRoute.selectedPortOfLoading, pod: FutureRoute.selectedPortOfDischarge },
                            });
                            const services = Array.isArray(response.data)
                              ? response.data.map(service => service.service)
                              : [];
                            updatedSections[index].availableServices = services;
                            updatedSections[index].selectedService = '';
                          } catch (error) {
                            console.error('Error fetching services for carrier:', error);
                            updatedSections[index].availableServices = [];
                            updatedSections[index].selectedService = '';
                          }
                        }
                        setFutureRoute(prev => ({ ...prev, carrierSections: updatedSections }));
                      }}
                    >
                      <option value="">-- Select Carrier --</option>
                      {(section.availableCarriers || []).map(carrier => (
                        <option key={carrier.scac_code} value={carrier.scac_code}>
                          {carrier.carrier_name} ({carrier.scac_code})
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group controlId={`service-${section.id}`}>
                    <Form.Label>Service</Form.Label>
                    <Form.Control as="select"
                      value={section.selectedService || ''}
                      onChange={(e) => future_handleCarrierServiceSection_ServiceChange(e, index)}
                    >
                      <option value="">-- Select Service --</option>
                      {(section.availableServices || []).map(service => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </Form.Control>
                    {section.missingServiceError && (
                      <Form.Text className="text-muted">{section.missingServiceError}</Form.Text>
                    )}
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group controlId={`sailingPriority-${section.id}`}>
                    <Form.Label>Sailing Priority</Form.Label>
                    <Form.Control
                      as="select"
                      value={section.sailingPriority || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const updatedSections = [...FutureRoute.carrierSections];
                        if (updatedSections.some((s, idx) => idx !== index && s.sailingPriority === newValue)) {
                          updatedSections[index] = {
                            ...section,
                            sailingPriority: newValue,
                            sailingPriorityError: 'Duplicate priority selected.'
                          };
                        } else {
                          updatedSections[index] = {
                            ...section,
                            sailingPriority: newValue,
                            sailingPriorityError: ''
                          };
                        }
                        setFutureRoute(prev => ({ ...prev, carrierSections: updatedSections }));
                      }}
                    >
                      <option value="">Select Sailing Priority</option>
                      {sailingPriorityOptions.map((option) => (
                        <option key={option.key} value={option.value}>{option.key}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>
              <div className="nested-cutoff-section">
                <Button
                  variant="link"
                  onClick={() => future_toggleCutoffSection(index)}
                  className="w-100 text-start mb-3"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h2 className="mb-0 cutoff-header">Cutoff Dates and Times</h2>
                    {section.showCutoffSection ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </Button>
                {section.showCutoffSection && (
                  <div>
                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group controlId="bookingCutoff" className="mb-3">
                          <Form.Label>Booking Cutoff Offset Days From Port ETD</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.bookingCutoff || ''}
                            onChange={e => {
                              const updatedSection = { ...section, bookingCutoff: e.target.value };
                              const newSections = [...FutureRoute.carrierSections];
                              newSections[index] = updatedSection;
                              setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                            }}
                            placeholder="Enter Booking Cutoff"
                          />
                        </Form.Group>
                        <Form.Group controlId="bookingCutoffTime" className="mb-3">
                          <Form.Label>Booking Cutoff Time</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="text"
                              placeholder="HH"
                              value={section.bookingCutoffTime?.hh || '12'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  bookingCutoffTime: { ...section.bookingCutoffTime, hh: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginRight: '5px' }}
                            />
                            <span>:</span>
                            <Form.Control
                              type="text"
                              placeholder="MM"
                              value={section.bookingCutoffTime?.mm || '00'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  bookingCutoffTime: { ...section.bookingCutoffTime, mm: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                            />
                            <Form.Select
                              value={section.bookingCutoffTime?.meridiem || 'AM'}
                              onChange={(e) => {
                                const updatedSection = {
                                  ...section,
                                  bookingCutoffTime: { ...section.bookingCutoffTime, meridiem: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '80px' }}
                            >
                              <option>AM</option>
                              <option>PM</option>
                            </Form.Select>
                          </div>
                        </Form.Group>
                        <Form.Group controlId="cargoCutoff" className="mb-3">
                          <Form.Label>Cargo Cutoff Offset Days From Port ETD</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.cargoCutoff || ''}
                            onChange={e => {
                              const updatedSection = { ...section, cargoCutoff: e.target.value };
                              const newSections = [...FutureRoute.carrierSections];
                              newSections[index] = updatedSection;
                              setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                            }}
                            placeholder="Enter Cargo Cutoff"
                          />
                        </Form.Group>
                        <Form.Group controlId="cargoCutoffTime" className="mb-3">
                          <Form.Label>Cargo Cutoff Time</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="text"
                              placeholder="HH"
                              value={section.cargoCutoffTime?.hh || '12'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  cargoCutoffTime: { ...section.cargoCutoffTime, hh: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginRight: '5px' }}
                            />
                            <span>:</span>
                            <Form.Control
                              type="text"
                              placeholder="MM"
                              value={section.cargoCutoffTime?.mm || '00'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  cargoCutoffTime: { ...section.cargoCutoffTime, mm: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                            />
                            <Form.Select
                              value={section.cargoCutoffTime?.meridiem || 'AM'}
                              onChange={(e) => {
                                const updatedSection = {
                                  ...section,
                                  cargoCutoffTime: { ...section.cargoCutoffTime, meridiem: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '80px' }}
                            >
                              <option>AM</option>
                              <option>PM</option>
                            </Form.Select>
                          </div>
                        </Form.Group>
                        <Form.Group controlId="documentationCutoff" className="mb-3">
                          <Form.Label>Documentation Cutoff Offset Days From Port ETD</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.documentationCutoff || ''}
                            onChange={e => {
                              const updatedSection = { ...section, documentationCutoff: e.target.value };
                              const newSections = [...FutureRoute.carrierSections];
                              newSections[index] = updatedSection;
                              setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                            }}
                            placeholder="Enter Documentation Cutoff"
                          />
                        </Form.Group>
                        <Form.Group controlId="documentationCutoffTime" className="mb-3">
                          <Form.Label>Documentation Cutoff Time</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="text"
                              placeholder="HH"
                              value={section.documentationCutoffTime?.hh || '12'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  documentationCutoffTime: { ...section.documentationCutoffTime, hh: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginRight: '5px' }}
                            />
                            <span>:</span>
                            <Form.Control
                              type="text"
                              placeholder="MM"
                              value={section.documentationCutoffTime?.mm || '00'}
                              onChange={e => {
                                const updatedSection = {
                                  ...section,
                                  documentationCutoffTime: { ...section.documentationCutoffTime, mm: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '60px', marginLeft: '5px', marginRight: '5px' }}
                            />
                            <Form.Select
                              value={section.documentationCutoffTime?.meridiem || 'AM'}
                              onChange={(e) => {
                                const updatedSection = {
                                  ...section,
                                  documentationCutoffTime: { ...section.documentationCutoffTime, meridiem: e.target.value }
                                };
                                const newSections = [...FutureRoute.carrierSections];
                                newSections[index] = updatedSection;
                                setFutureRoute(prev => ({ ...prev, carrierSections: newSections }));
                              }}
                              style={{ width: '80px' }}
                            >
                              <option>AM</option>
                              <option>PM</option>
                            </Form.Select>
                          </div>
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group controlId="etdWeekday">
                          <Form.Label>ETD Weekday</Form.Label>
                          <Form.Control
                            as="select"
                            value={section.etdWeekday || ''}
                            onChange={(e) => {
                              const updated = [...FutureRoute.carrierSections];
                              updated[index] = { ...updated[index], etdWeekday: e.target.value };
                              setFutureRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          >
                            <option value="">Select ETD Weekday</option>
                            {section.poletdweekdayoptions?.map((weekday) => (
                              <option key={weekday} value={weekday}>{weekday}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="etaWeekday">
                          <Form.Label>ETA Weekday</Form.Label>
                          <Form.Control
                            as="select"
                            value={section.etaWeekday || ''}
                            onChange={(e) => {
                              const updated = [...FutureRoute.carrierSections];
                              updated[index] = { ...updated[index], etaWeekday: e.target.value };
                              setFutureRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          >
                            <option value="">Select ETA Weekday</option>
                            {section.podetaweekdayoptions?.map((weekday) => (
                              <option key={weekday} value={weekday}>{weekday}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="cargoAvailabilityWeekday">
                          <Form.Label>Cargo Availability Weekday</Form.Label>
                          <Form.Control
                            as="select"
                            value={section.cargoAvailabilityWeekday || ''}
                            onChange={(e) => {
                              const updated = [...FutureRoute.carrierSections];
                              updated[index] = { ...updated[index], cargoAvailabilityWeekday: e.target.value };
                              setFutureRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          >
                            <option value="">Select Cargo Availability</option>
                            {daysOfWeek.map((day) => (
                              <option key={day} value={day}>{day}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="portDwellTime">
                          <Form.Label>Port Dwell Time</Form.Label>
                          <Form.Control
                            type="number"
                            value={section.portDwellTime || 0}
                            onChange={(e) => {
                              const updated = [...FutureRoute.carrierSections];
                              updated[index] = { ...updated[index], portDwellTime: Number(e.target.value) };
                              setFutureRoute(prev => ({ ...prev, carrierSections: updated }));
                            }}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}


          <div className="text-center mb-3">
            <Button
              variant="primary"
              onClick={future_handleAddCarrierService}
              disabled={FutureRoute.carrierSections.length >= 3}
              style={FutureRoute.carrierSections.length >= 3 ? { backgroundColor: 'grey', borderColor: 'grey', cursor: 'not-allowed' } : {}}
            >
              <FaPlus className="me-1" /> Add Carrier Service
            </Button>
          </div>

          {/* Form section for selecting Effective Date and Expiration Date */}
          <div className="form-section shadow-sm row mb-3">
            <div className="col-md-6">
              <Form.Group controlId="effectiveDate" className="mb-3">
                <Form.Label>Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    FutureRoute.effectiveDate
                      ? new Date(FutureRoute.effectiveDate).toISOString().slice(0, 10)
                      : ""
                  }
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const dateError = isMoreThanOneDayInPast(newDate) ? 
                      "Effective date cannot be more than 1 day in the past." : null;
                    setFutureRoute(prev => ({ 
                      ...prev, 
                      effectiveDate: newDate,
                      effectiveDateError: dateError
                    }));
                  }}
                />
                {/* Add contextual hint text for Future tab */}
                <Form.Text className="text-muted">
                  A past effective date will activate this version immediately, replace the current Live version, and move it to the Live tab.
                </Form.Text>
                {FutureRoute.effectiveDateError && (
                  <Form.Text className="text-muted text-warning">
                    {FutureRoute.effectiveDateError}
                  </Form.Text>
                )}
              </Form.Group>
            </div>
          </div>

          {/* Save and Cancel buttons */}
          <Button
            variant="primary"
            onClick={() => future_handleSaveEditRoute(isAddForm ? 'add' : 'edit')}
            disabled={FutureRoute.isSaving || (FutureRoute.existingConversionRequest && (!FutureRoute.existingConversionRequest.due_for_conversion || FutureRoute.existingConversionRequest.converted)) || FutureRoute.routeNotFound}
          >
            {FutureRoute.isSaving ? <Spinner as="span" animation="border" size="sm" /> : <FaSave className="me-2" />}
            Save
          </Button>
          <Button variant="secondary" onClick={() => navigate(-1)} className="ms-2">
            <FaTimes className="me-2" />Cancel
          </Button>

        </div>
      )}



      <Modal show={LiveRoute.showSailPriorityHelp} onHide={() => setLiveRoute(prev => ({ ...prev, showSailPriorityHelp: false }))}>
        <Modal.Header closeButton>
          <Modal.Title>Sailing Priority Help</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Select Main to identify the first priority Carrier Service, Second Main for the second priority Carrier Service, and Main Backup in case the Main produces no schedules for Main, and Second Main Backup in case the Second Main produces no schedules.
        </Modal.Body>
      </Modal>

      <Modal show={LiveRoute.showCFSOriginHelp} onHide={() => setLiveRoute(prev => ({ ...prev, showCFSOriginHelp: false }))}>
        <Modal.Header closeButton>
          <Modal.Title>CFS Origin Help</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Available CFS Origin locations for the selected POL are defined by the pre-carriage rules in the <a href="/rules/carriage" target="_blank" rel="noopener noreferrer">Carriage Rules</a> page. If you do not see a CFS Origin location here, please add it as a pre-carriage in the Carriage Rules.
        </Modal.Body>
      </Modal>

      <Modal show={LiveRoute.showCFSDestinationHelp} onHide={() => setLiveRoute(prev => ({ ...prev, showCFSDestinationHelp: false }))}>
        <Modal.Header closeButton>
          <Modal.Title>CFS Destination Help</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Available CFS Destination locations for the selected POD are defined by the on-carriage rules in the <a href="/rules/carriage" target="_blank" rel="noopener noreferrer">Carriage Rules</a> page. If you do not see a CFS Destination location here, please add it as a on-carriage in the Carriage Rules.
        </Modal.Body>
      </Modal>

      <Modal show={LiveRoute.showCoLoadModal} onHide={live_handleCloseCoLoadModal}>
        <Modal.Header closeButton>
          <Modal.Title>Convert to Co-Load Route</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {LiveRoute.conversionSuccess ? (
            <p>DCC route conversion request successfully submitted.</p>
          ) : LiveRoute.conversionCancellationSuccess ? (
            <p>DCC route conversion request successfully cancelled.</p>
          ) : (
            <>
              {LiveRoute.conversionError && (
                <Alert variant="danger">
                  {LiveRoute.conversionError}
                </Alert>
              )}
              <p>
                {LiveRoute.existingConversionRequest ? (
                  LiveRoute.existingConversionRequest.due_for_conversion ? (
                    <span>There is an existing Co-Load Route conversion request for this route. Would you like to update or cancel the existing request with the following information?:</span>
                  ) : (
                    <span>The following conversion request has already passed and can not be edited:</span>
                  )
                ) : (
                  <span>Would you like to create new Co-Load Routes using the following information?:</span>
                )
                }
              </p>
              <div>
                <Form.Group controlId="coLoadRoutes">
                  <Form.Label>Co-Load Routes</Form.Label>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <p>
                      {LiveRoute.combinations?.map((token, idx) => (
                        <Badge pill variant="primary" key={idx} className="me-2 mb-2">
                          {token}
                        </Badge>
                      ))}
                    </p>
                  </div>
                  <Form.Control
                    type="hidden"
                    value={LiveRoute.combinations?.join(',')}
                    readOnly
                  />
                </Form.Group>
              </div>
              <div class="mt-2">
                <Form.Group controlId="coLoadProvider">
                  <Form.Label>Co-Load Provider</Form.Label>
                  <Form.Control
                    as="select"
                    value={LiveRoute.selectedCoLoadProvider ? LiveRoute.selectedCoLoadProvider : ''}
                    onChange={(e) => {
                      const provider = LiveRoute.coLoadProviders.find(p => p.provider_id.toString() === e.target.value);
                      setLiveRoute(prev => ({ ...prev, selectedCoLoadProvider: provider ? provider.provider_id : null }));
                    }}
                  >
                    <option value="">Select Co-Load Provider</option>
                    {LiveRoute.coLoadProviders.map(provider => (
                      <option key={provider.provider_id} value={provider.provider_id}>
                        {provider.provider_name} ({provider.provider_scac})
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
              <div class="mt-2">
                <Form.Group controlId="conversionStartDate">
                  <Form.Label className='me-2'>Conversion Start Date</Form.Label>
                  <DatePicker
                    selected={LiveRoute.selectedConversionStartDate}
                    onChange={date => setLiveRoute(prev => ({ ...prev, selectedConversionStartDate: date }))}
                    dateFormat="MM/dd/yyyy"
                    className="form-control"
                  />
                </Form.Group>
              </div>
            </>
          )}
        </Modal.Body>
        {!LiveRoute.conversionSuccess && !LiveRoute.conversionCancellationSuccess && (
          <Modal.Footer>
            <Button variant="secondary" onClick={live_handleCloseCoLoadModal}>
              Back
            </Button>
            <Button variant="primary" onClick={live_handleConvertToCoload}>
              Convert to Co-Load Route
            </Button>
            {LiveRoute.existingConversionRequest && LiveRoute.existingConversionRequest.due_for_conversion && (
              <Button variant="danger" onClick={live_handleConvertToCoload}>
                Cancel Convert to Co-Load Route Request
              </Button>
            )}
          </Modal.Footer>
        )}
      </Modal>




    </div>
  );
};

export default AddEditDCCRouteForm;